
import {
  BarChart3,
  BookOpen,
  Inbox,
  Landmark,
  LayoutDashboard,
  Link2,
  LogOut,
  Monitor,
  Moon,
  Newspaper,
  Plus,
  Sun,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import {
  ApprovalIcon,
  CategoryIcon,
  KpiIcon,
  MessengerIcon,
  NotificationsIcon,
  PersonIcon,
  AdminIcon,
  SettingsIcon,
  SprintIcon,
  TaskIcon,
} from '@/shared/config';
import { adminNavGroups, isUnfinishedAdminItem } from '@/shared/config/admin-sections';

export type CommandSection = 'nav' | 'create' | 'admin' | 'prefs' | 'account';

export interface Command {
  id: string;
  section: CommandSection;
  icon: LucideIcon;
  label: string;
  description?: string;
  keywords?: string[];
  hint?: string;
  to?: string;
  run?: () => void;
  stay?: boolean;
}

export interface CommandContext {
  hasPermission: (permission: string) => boolean;
  canWiki: boolean;
  themeMode: 'light' | 'dark' | 'system';
  setThemeMode: (mode: 'light' | 'dark' | 'system') => void;
  logout: () => void;
  copyCurrentLink: () => void;
  clearHistory: (() => void) | null;
}

const THEME_LABELS = { light: 'светлая', dark: 'тёмная', system: 'как в системе' } as const;

export function buildCommands(ctx: CommandContext): Command[] {
  const { hasPermission } = ctx;
  const isStaff = hasPermission('view_tickets');
  const list: Command[] = [];

  const nav = (c: Omit<Command, 'section'>) => list.push({ ...c, section: 'nav' });

  nav({
    id: 'a-mine',
    icon: Inbox,
    label: 'Мои обращения',
    to: '/tickets',
    description: 'Заявки, которые вы создали или можете видеть',
    keywords: ['заявки', 'портал', 'мои заявки'],
  });
  nav({
    id: 'a-approvals',
    icon: ApprovalIcon,
    label: 'Согласования',
    to: '/approvals',
    description: 'Заявки, которые ждут вашего решения',
    keywords: ['согласовать', 'одобрить', 'approve'],
  });
  if (isStaff) {
    nav({
      id: 'a-workspace',
      icon: LayoutDashboard,
      label: 'Рабочий стол',
      to: '/tickets/workspace',
      description: 'Заявки в работе у вас и вашей группы',
      keywords: ['очередь', 'список заявок', 'workspace'],
    });
  }
  if (isStaff && hasPermission('view_sprints')) {
    nav({
      id: 'a-sprints',
      icon: SprintIcon,
      label: 'Спринты',
      to: '/sprints',
      description: 'Доска задач текущего спринта',
      keywords: ['доска', 'канбан', 'board'],
    });
    nav({
      id: 'a-my-tasks',
      icon: TaskIcon,
      label: 'Мои задачи',
      to: '/sprints?mine=1',
      description: 'Доска, где оставлены только ваши задачи',
      keywords: ['задачи', 'назначено мне'],
    });
    nav({ id: 'a-sprint-calendar', icon: SprintIcon, label: 'Календарь спринта', to: '/sprints?view=calendar' });
    nav({
      id: 'a-sprint-timeline',
      icon: SprintIcon,
      label: 'Таймлайн спринта',
      to: '/sprints?view=timeline',
      keywords: ['гант', 'gantt', 'план'],
    });
    nav({ id: 'a-sprint-report', icon: BarChart3, label: 'Отчёт спринта', to: '/sprints?view=report' });
  }
  if (hasPermission('view_curated_tickets')) {
    nav({
      id: 'a-committee',
      icon: Landmark,
      label: 'Комитет',
      to: '/committee',
      description: 'Изменения на рассмотрении комитета',
      keywords: ['изменения', 'cab'],
    });
  }
  if (isStaff) {
    nav({
      id: 'a-messenger',
      icon: MessengerIcon,
      label: 'Мессенджер',
      to: '/messenger',
      description: 'Переписка с коллегами и каналы групп',
      keywords: ['чат', 'сообщения', 'написать'],
    });
    nav({
      id: 'a-analytics',
      icon: BarChart3,
      label: 'Аналитика',
      to: '/analytics',
      description: 'Отчёты по заявкам за выбранный период',
      keywords: ['отчёт', 'статистика', 'графики'],
    });
    nav({
      id: 'a-kpi',
      icon: KpiIcon,
      label: 'KPI инженеров',
      to: '/kpi',
      description: 'Начисления и рейтинг инженеров за месяц',
      keywords: ['рейтинг', 'баллы', 'лидерборд'],
    });
  }
  nav({
    id: 'a-catalog',
    icon: CategoryIcon,
    label: 'Каталог услуг',
    to: '/categories',
    description: 'Категории, из которых заводится обращение',
    keywords: ['категории', 'услуги'],
  });
  if (ctx.canWiki) {
    nav({
      id: 'a-wiki',
      icon: BookOpen,
      label: 'Инструкции',
      to: '/instructions',
      description: 'Статьи справочного центра',
      keywords: ['вики', 'wiki', 'справка', 'помощь', 'статьи'],
    });
  }
  nav({
    id: 'a-notifications',
    icon: NotificationsIcon,
    label: 'Уведомления',
    to: '/notifications',
    keywords: ['колокольчик', 'оповещения'],
  });
  nav({
    id: 'a-changelog',
    icon: Newspaper,
    label: 'Что нового',
    to: '/changelog',
    description: 'Изменения портала по выпускам',
    keywords: ['обновления', 'релиз', 'версия', 'changelog'],
  });
  nav({ id: 'a-profile', icon: PersonIcon, label: 'Мой профиль', to: '/users/me', keywords: ['аккаунт', 'аватар'] });
  nav({
    id: 'a-settings',
    icon: SettingsIcon,
    label: 'Настройки',
    to: '/settings',
    keywords: ['параметры', 'уведомления на почту', 'шаблоны ответов'],
  });

  list.push({
    id: 'a-new',
    section: 'create',
    icon: Plus,
    label: 'Новое обращение',
    to: '/categories',
    description: 'Выбрать услугу и завести заявку',
    keywords: ['создать заявку', 'новая заявка', 'обратиться'],
  });

  for (const group of adminNavGroups) {
    for (const item of group.items) {
      if (isUnfinishedAdminItem(item) || !item.permissions.some(hasPermission)) continue;
      list.push({
        id: `adm-${item.to}`,
        section: 'admin',
        icon: item.Icon,
        label: item.label,
        hint: 'админка',
        to: item.to,
        description: `Администрирование · ${group.label}`,
        keywords: ['админка', 'администрирование', 'настроить'],
      });
    }
  }
  if (list.some((c) => c.section === 'admin')) {
    list.push({
      id: 'a-admin',
      section: 'admin',
      icon: AdminIcon,
      label: 'Администрирование',
      to: list.find((c) => c.section === 'admin')?.to ?? '/admin',
      keywords: ['админка', 'admin'],
    });
  }

  const theme = (mode: 'light' | 'dark' | 'system', icon: LucideIcon, keywords: string[]) =>
    list.push({
      id: `theme-${mode}`,
      section: 'prefs',
      icon,
      label: `Тема: ${THEME_LABELS[mode]}`,
      hint: ctx.themeMode === mode ? 'сейчас' : undefined,
      description: 'Оформление запоминается в этом браузере',
      keywords: ['тема', 'оформление', 'цвет', ...keywords],
      run: () => ctx.setThemeMode(mode),
      stay: true,
    });
  theme('light', Sun, ['светлая', 'день', 'light']);
  theme('dark', Moon, ['тёмная', 'ночь', 'dark']);
  theme('system', Monitor, ['системная', 'авто', 'system']);

  list.push({
    id: 'copy-link',
    section: 'account',
    icon: Link2,
    label: 'Скопировать ссылку на эту страницу',
    keywords: ['поделиться', 'url', 'адрес'],
    run: ctx.copyCurrentLink,
  });
  if (ctx.clearHistory) {
    list.push({
      id: 'clear-history',
      section: 'account',
      icon: Trash2,
      label: 'Очистить недавнее в поиске',
      keywords: ['история', 'забыть'],
      run: ctx.clearHistory,
      stay: true,
    });
  }
  list.push({
    id: 'logout',
    section: 'account',
    icon: LogOut,
    label: 'Выйти из аккаунта',
    keywords: ['выход', 'logout', 'разлогиниться'],
    run: ctx.logout,
  });

  return list;
}
